import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa';
import { usePayment } from '../context/PaymentContext';

const OrderSuccessModal = ({ isOpen, onClose, orderItems, userSessionId }) => {
  const navigate = useNavigate();
  const { setOrderForPayment } = usePayment();
  const [timeRemaining, setTimeRemaining] = useState(7 * 60); // 7 minutes in seconds
  const [isExpired, setIsExpired] = useState(false);
  
  // Start timer when modal is opened
  useEffect(() => {
    let timer;
    if (isOpen && !isExpired && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsExpired(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isOpen, isExpired, timeRemaining]);
  
  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setTimeRemaining(7 * 60);
      setIsExpired(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;

  // Add a class for styling and transitions
  const modalClassName = `order-success-modal ${isOpen ? 'visible' : ''}`;

  const handleProceedToPay = () => {
    // Store the order data in our global PaymentContext
    // This is much more reliable than localStorage or router state
    setOrderForPayment(orderItems, userSessionId);
    
    // Also store in localStorage as a backup
    const orderData = {
      originalItems: orderItems,
      userSessionId: userSessionId,
      timestamp: Date.now()
    };
    localStorage.setItem('cinemaSnacksOrderData', JSON.stringify(orderData));
    
    // Set a session storage flag that the payment page will check
    // This is the most reliable way to ensure the payment page knows an order is active
    sessionStorage.setItem('hasActiveOrder', 'true');
    
    // Close the modal first
    onClose();
    
    // Navigate directly to payment page
    navigate('/pay');
  };

  const handleOrderCancelled = () => {
    // Close modal and redirect to snacks page
    onClose();
    navigate('/snacks');
  };

  // Format remaining time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  //console/log('OrderSuccessModal rendering, isOpen:', isOpen); // Debug log

  return (
    <div className="modal-overlay">
      <div className={modalClassName}>
        {!isExpired ? (
          <>
            <div className="modal-header">
              <FaCheckCircle className="success-icon" />
              <h2>Snacks Order Initiated!</h2>
            </div>
            <div className="modal-body">
              <p>Your order has been initiated successfully.</p>
              
              <div className="time-warning">
                <FaClock />
                <p>You have <strong>{formatTime(timeRemaining)} minutes</strong> to make payment and complete the order</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="proceed-to-pay-btn" onClick={handleProceedToPay}>
                Proceed to Pay
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-header cancelled">
              <FaTimesCircle className="cancel-icon" />
              <h2>Order Cancelled</h2>
            </div>
            
            <div className="modal-body">
              <p>Your order has been cancelled because the payment time has expired.</p>
            </div>
            
            <div className="modal-footer">
              <button className="ok-btn" onClick={handleOrderCancelled}>
                OK
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSuccessModal;
